import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createTheme } from '@mui/material/styles';

// ── Original icons ──────────────────────────────────────────────
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import MapIcon from '@mui/icons-material/Map';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import GroupIcon from '@mui/icons-material/Group';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import { AttachMoney, PaymentsOutlined, RequestQuoteOutlined } from '@mui/icons-material';
import BusAlert from '@mui/icons-material/BusAlert';
import Assignment from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ReceiptIcon from '@mui/icons-material/Receipt';
// import PersonIcon from '@mui/icons-material/Person';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

// ── New icons ────────────────────────────────────────────────────
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';
import DevicesIcon from '@mui/icons-material/Devices';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SecurityIcon from '@mui/icons-material/Security';
import DirectionsBusFilledIcon from '@mui/icons-material/DirectionsBusFilled';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BuildIcon from '@mui/icons-material/Build';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import BadgeIcon from '@mui/icons-material/Badge';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SummarizeIcon from '@mui/icons-material/Summarize';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import TuneIcon from '@mui/icons-material/Tune';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import RouteIcon from '@mui/icons-material/Route';
import SpeedIcon from '@mui/icons-material/Speed';
import HeatPumpIcon from '@mui/icons-material/HeatPump';
import PolicyIcon from '@mui/icons-material/Policy';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import SchoolIcon from '@mui/icons-material/School';

import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { useDemoRouter } from '@toolpad/core/internal';
// import { ReactComponent as Logo } from '../resources/eseat.svg';
import Logo from '../resources/logo.png';


import Dashboard from './Dashboard';
import FinancePage from './FinancePage';
import DailyChannelReconciliationPage from './DailyChannelReconciliationPage';
import RefundRegisterPage from './RefundRegisterPage';
import TravelCardLedgerSummaryPage from './TravelCardLedgerSummaryPage';
import ConductorPOSRouteAssignment from './ConductorPOSRouteAssignment';
import BusBreakdownRefundPage from './BusBreakdownRefundPage';
import BusBreakdownAnalysisPage from './BusBreakdownAnalysisPage';
import AIAlertAnalysisPage from './AIAlertAnalysisPage';
import RevenueTrendsPage from './RevenueTrendsPage';
import RouteSchedulePage from './RouteSchedulePage';
import RiskExceptionsPage from './RiskExceptionsPage';
import BusProfitabilityPage from './BusProfitabilityPage';
import ChannelPerformancePage from './ChannelPerformancePage';
import ScheduleEfficiencyPage from './ScheduleEfficiencyPage';


// import CustomAlert from './Parts/CustomAlert';


const NAVIGATION = {
  SuperAdmin: [
    // 1.0 Authentication & Access
    {
      kind: 'header',
      title: 'Access',
    },
    {
      segment: 'dashboard',
      title: 'Dashboard',
      icon: <DashboardIcon />,
    },
    {
      segment: 'finance-page',
      title: 'Finance',
      icon: <LocalAtmIcon />,
    },
    {
      segment: 'daily-channel-reconciliation',
      title: 'Daily Channel Reconciliation',
      icon: <CalendarMonthIcon />,
    },
    {
      segment: 'refund-register-page',
      title: 'Refund Register',
      icon: <MoneyOffIcon />,
    },
    {
      segment: 'travel-card-ledger-summary',
      title: 'Travel Card Ledger Summary',
      icon: <SummarizeIcon />,
    },
    {
      segment: 'conductor-pos-route-assignment',
      title: 'Conductor POS Route Assignment',
      icon: <Assignment />,
    },
    {
      segment: 'bus-breakdown-refund-page',
      title: 'Bus Breakdown Refund',
      icon: <BuildIcon />,
    },
    {
      segment: 'bus-breakdown-analysis',
      title: 'Bus Breakdown Analysis',
      icon: <ListAltIcon />,
    },
    {
      segment: 'ai-alert-analysis',
      title: 'AI Alert Analysis',
      icon: <AnalyticsIcon />,
    },
    {
      segment: 'revenue-trends-page',
      title: 'Revenue Trends',
      icon: <AutoGraphIcon />,
    },
    {
      segment: 'route-schedule-page',
      title: 'Route Schedule',
      icon: <RouteIcon />,
    },
    {
      segment: 'risk-exceptions-page',
      title: 'Risk Exceptions',
      icon: <EscalatorWarningIcon />,
    },
    {
      segment: 'bus-profitability-page',
      title: 'Bus Profitability',
      icon: <DirectionsBusIcon />,
    },
    {
      segment: 'channel-performance-page',
      title: 'Channel Performance',
      icon: <AccountTreeIcon />,
    },
    {
      segment: 'schedule-efficiency-page',
      title: 'Schedule Efficiency',
      icon: <DateRangeIcon />,
    },


    // {
    //   segment: 'profile',
    //   title: 'User Profile',
    //   icon: <PersonIcon />,
    // },

    // 2.0 System Administration
    {
      kind: 'header',
      title: 'System Administration',
    },
    {
      segment: 'user-management',
      title: 'User Management',
      icon: <ManageAccountsIcon />,
      children: [
        { segment: 'users', title: 'Users', icon: <GroupIcon /> },
        { segment: 'role-assignments', title: 'Role Assignments', icon: <SwitchAccountIcon /> },
        { segment: 'depot-assignments', title: 'Depot Assignments', icon: <WarehouseIcon /> },
        { segment: 'counter-assignments', title: 'Counter Assignments', icon: <StorefrontIcon /> },
        { segment: 'device-assignments', title: 'Device Assignments', icon: <DevicesIcon /> },
        { segment: 'user-status', title: 'User Status', icon: <VerifiedUserIcon /> },
      ],
    },
    {
      segment: 'role-permission',
      title: 'Role & Permission',
      icon: <AdminPanelSettingsIcon />,
      children: [
        { segment: 'roles', title: 'Roles', icon: <BadgeIcon /> },
        { segment: 'permission-matrix', title: 'Permission Matrix', icon: <TuneIcon /> },
        { segment: 'menu-access', title: 'Menu Access Control', icon: <ListAltIcon /> },
        { segment: 'approval-rights', title: 'Approval Rights', icon: <CheckCircleOutlineIcon /> },
      ],
    },
    {
      segment: 'organization-setup',
      title: 'Organization Setup',
      icon: <BusinessIcon />,
      children: [
        { segment: 'head-office', title: 'Head Office', icon: <AccountBalanceIcon /> },
        { segment: 'regions', title: 'Regions', icon: <MapIcon /> },
        { segment: 'depots', title: 'Depots', icon: <WarehouseIcon /> },
        { segment: 'counters', title: 'Counters', icon: <StorefrontIcon /> },
        { segment: 'government-agencies', title: 'Government Agencies', icon: <PolicyIcon /> },
        { segment: 'external-partners', title: 'External Partners', icon: <HandshakeIcon /> },
      ],
    },
    {
      segment: 'master-data',
      title: 'Master Data',
      icon: <ViewComfyIcon />,
      children: [
        { segment: 'routes', title: 'Routes', icon: <RouteIcon /> },
        { segment: 'schedules', title: 'Schedules', icon: <CalendarMonthIcon /> },
        { segment: 'stages-stops', title: 'Stages / Stops', icon: <LocationOnIcon /> },
        { segment: 'fare-matrices', title: 'Fare Matrices', icon: <AttachMoneyIcon /> },
        { segment: 'bus-types', title: 'Bus Types', icon: <DirectionsBusIcon /> },
        { segment: 'travel-card-products', title: 'Travel Card Products', icon: <CreditCardIcon /> },
        { segment: 'season-ticket-products', title: 'Season Ticket Products', icon: <CardMembershipIcon /> },
        { segment: 'government-pass-products', title: 'Government Pass Products', icon: <BadgeIcon /> },
        { segment: 'booking-fee-rules', title: 'Booking Fee Rules', icon: <RequestQuoteOutlined /> },
        { segment: 'refund-rules', title: 'Refund Rules', icon: <MoneyOffIcon /> },
        { segment: 'alert-rules', title: 'Alert Rules', icon: <WarningAmberIcon /> },
        { segment: 'status-codes', title: 'Status / Reason Codes', icon: <ListAltIcon /> },
      ],
    },
    {
      segment: 'device-administration',
      title: 'Device Administration',
      icon: <DevicesIcon />,
      children: [
        { segment: 'pos-devices', title: 'POS Devices', icon: <PointOfSaleIcon /> },
        { segment: 'validators', title: 'Validator / Card Readers', icon: <CreditCardIcon /> },
        { segment: 'counter-devices', title: 'Counter Devices', icon: <StorefrontIcon /> },
        { segment: 'device-health', title: 'Device Health', icon: <MonitorHeartIcon /> },
        { segment: 'device-sync', title: 'Device Sync Status', icon: <SwapHorizIcon /> },
      ],
    },
    {
      segment: 'financial-config',
      title: 'Financial Configuration',
      icon: <AccountBalanceIcon />,
      children: [
        { segment: 'posting-rules', title: 'Posting Rules', icon: <TuneIcon /> },
        { segment: 'settlement-cycles', title: 'Settlement Cycles', icon: <DateRangeIcon /> },
        { segment: 'bank-accounts', title: 'Bank Accounts', icon: <SavingsIcon /> },
        { segment: 'payment-channels', title: 'Payment Channels', icon: <PaymentsOutlined /> },
        { segment: 'finance-nodes', title: 'Finance Nodes', icon: <AccountTreeIcon /> },
        { segment: 'approval-thresholds', title: 'Approval Thresholds', icon: <PriceCheckIcon /> },
      ],
    },
    {
      segment: 'audit-security',
      title: 'Audit & Security',
      icon: <SecurityIcon />,
      children: [
        { segment: 'audit-log', title: 'Audit Log', icon: <Assignment /> },
        { segment: 'login-history', title: 'Login History', icon: <HistoryIcon /> },
        { segment: 'failed-logins', title: 'Failed Login Attempts', icon: <ErrorOutlineIcon /> },
        { segment: 'user-activity', title: 'User Activity Log', icon: <PersonSearchIcon /> },
        { segment: 'data-change-log', title: 'Data Change Log', icon: <SwapVertIcon /> },
      ],
    },

    // 3.0 Fleet & Operations
    {
      kind: 'header',
      title: 'Fleet & Operations',
    },
    {
      segment: 'bus-management',
      title: 'Bus Management',
      icon: <DirectionsBusFilledIcon />,
      children: [
        { segment: 'bus-master', title: 'Bus Master', icon: <DirectionsBusIcon /> },
        { segment: 'bus-ownership', title: 'Bus Ownership Transfers', icon: <SwapHorizIcon /> },
        { segment: 'bus-status', title: 'Bus Status', icon: <MonitorHeartIcon /> },
        { segment: 'bus-allocation-history', title: 'Bus Allocation History', icon: <HistoryIcon /> },
      ],
    },
    {
      segment: 'crew-management',
      title: 'Crew Management',
      icon: <GroupIcon />,
      children: [
        { segment: 'conductors', title: 'Conductors', icon: <AirlineSeatReclineNormalIcon /> },
        { segment: 'drivers', title: 'Drivers', icon: <DirectionsRunIcon /> },
        { segment: 'crew-assignments', title: 'Crew Assignments', icon: <Assignment /> },
        { segment: 'staff-status', title: 'Staff Status', icon: <VerifiedUserIcon /> },
      ],
    },
    {
      segment: 'route-operations',
      title: 'Route Operations',
      icon: <RouteIcon />,
      children: [
        { segment: 'route-master', title: 'Route Master', icon: <MapIcon /> },
        { segment: 'direction-mapping', title: 'Direction Mapping', icon: <AltRouteIcon /> },
        { segment: 'stage-sequencing', title: 'Stage Sequencing', icon: <LocationOnIcon /> },
        { segment: 'route-availability', title: 'Route Availability', icon: <EventAvailableIcon /> },
      ],
    },
    {
      segment: 'schedule-operations',
      title: 'Schedule Operations',
      icon: <CalendarMonthIcon />,
      children: [
        { segment: 'schedule-master', title: 'Schedule Master', icon: <DateRangeIcon /> },
        { segment: 'schedule-calendar', title: 'Schedule Calendar', icon: <EventAvailableIcon /> },
        { segment: 'schedule-allocation', title: 'Schedule Allocation', icon: <Assignment /> },
        { segment: 'schedule-exceptions', title: 'Schedule Exceptions', icon: <ErrorOutlineIcon /> },
      ],
    },
    {
      segment: 'trip-operations',
      title: 'Trip Operations',
      icon: <PlayArrowIcon />,
      children: [
        { segment: 'trip-instances', title: 'Trip Instances', icon: <ListAltIcon /> },
        { segment: 'bus-assignment', title: 'Bus Assignment', icon: <DirectionsBusIcon /> },
        { segment: 'crew-assignment', title: 'Crew Assignment', icon: <GroupIcon /> },
        { segment: 'trip-status', title: 'Trip Status', icon: <MonitorHeartIcon /> },
        { segment: 'cancellations', title: 'Cancellations', icon: <CancelOutlinedIcon /> },
        { segment: 'breakdown-events', title: 'Breakdown Events', icon: <BusAlert /> },
      ],
    },

    // 4.0 Counter Operations
    {
      kind: 'header',
      title: 'Counter Operations',
    },
    { segment: 'counter-home', title: 'Counter Home', icon: <DashboardIcon /> },
    { segment: 'new-booking', title: 'New Booking', icon: <AddCircleIcon /> },
    {
      segment: 'todays-bookings',
      title: "Today's Bookings",
      icon: <EventAvailableIcon />,
      children: [
        { segment: 'booking-register', title: 'Booking Register', icon: <ListAltIcon /> },
        { segment: 'booking-detail', title: 'Booking Detail', icon: <Assignment /> },
        { segment: 'reprint-ticket', title: 'Reprint Ticket', icon: <ReceiptIcon /> },
        { segment: 'reprint-receipt', title: 'Reprint Receipt', icon: <ReceiptIcon /> },
      ],
    },
    {
      segment: 'conductor-handover',
      title: 'Conductor Handover',
      icon: <HandshakeIcon />,
      children: [
        { segment: 'pending-bus-handovers', title: 'Pending Bus Handovers', icon: <PendingActionsIcon /> },
        { segment: 'completed-handovers', title: 'Completed Handovers', icon: <CheckCircleOutlineIcon /> },
      ],
    },
    {
      segment: 'cashier-handover',
      title: 'Cashier Handover',
      icon: <LocalAtmIcon />,
      children: [
        { segment: 'shift-summary', title: 'Current Shift Summary', icon: <SummarizeIcon /> },
        { segment: 'completed-cashier-handovers', title: 'Completed Handovers', icon: <CheckCircleOutlineIcon /> },
      ],
    },
    {
      segment: 'end-shift',
      title: 'End Shift',
      icon: <AccessTimeIcon />,
      children: [
        { segment: 'shift-checklist', title: 'Shift Checklist', icon: <ListAltIcon /> },
        { segment: 'shift-summary', title: 'Shift Summary', icon: <SummarizeIcon /> },
        { segment: 'shift-close', title: 'Shift Close', icon: <CancelScheduleSendIcon /> },
      ],
    },
    { segment: 'counter-exceptions', title: 'Counter Exceptions', icon: <ErrorOutlineIcon /> },
    { segment: 'counter-reports', title: 'Counter Reports', icon: <Assignment /> },


    // 5.0 Conductor / POS Operations
    {
      kind: 'header',
      title: 'POS Operations',
    },
    { segment: 'pos-home', title: 'POS Home', icon: <DashboardIcon /> },
    {
      segment: 'ticket-sales',
      title: 'Ticket Sales',
      icon: <LoyaltyIcon />,
      children: [
        { segment: 'cash-ticket', title: 'Cash Ticket Issue', icon: <LocalAtmIcon /> },
        { segment: 'digital-ticket', title: 'Digital Payment Ticket', icon: <PhoneAndroidIcon /> },
        { segment: 'travel-card-validation', title: 'Travel Card Validation', icon: <CreditCardIcon /> },
        { segment: 'season-ticket-validation', title: 'Season Ticket Validation', icon: <CardMembershipIcon /> },
        { segment: 'govt-pass-validation', title: 'Government Pass Validation', icon: <BadgeIcon /> },
      ],
    },
    {
      segment: 'trip-management',
      title: 'Trip Management',
      icon: <DirectionsBusIcon />,
      children: [
        { segment: 'start-trip', title: 'Start Trip', icon: <PlayArrowIcon /> },
        { segment: 'current-trip', title: 'Current Trip', icon: <DirectionsRunIcon /> },
        { segment: 'end-trip', title: 'End Trip', icon: <CancelScheduleSendIcon /> },
        { segment: 'shift-waybill', title: 'Shift / Waybill', icon: <Assignment /> },
      ],
    },
    {
      segment: 'booking-validation',
      title: 'Booking Validation',
      icon: <SearchIcon />,
      children: [
        { segment: 'advance-booking-check', title: 'Advance Booking Check', icon: <PersonSearchIcon /> },
        { segment: 'seat-validation', title: 'Seat Validation', icon: <AirlineSeatReclineNormalIcon /> },
      ],
    },
    { segment: 'breakdown-management', title: 'Breakdown Management', icon: <BuildIcon /> },
    { segment: 'cash-refund', title: 'Refunds', icon: <MoneyOffIcon /> },
    { segment: 'shift-summary', title: 'Shift Summary', icon: <SummarizeIcon /> },
    { segment: 'sync-offline', title: 'Sync / Offline Queue', icon: <SwapHorizIcon /> },
    { segment: 'device-status', title: 'Device Status', icon: <MonitorHeartIcon /> },

    // 6.0 Inspector Operations
    {
      kind: 'header',
      title: 'Inspector',
    },
    { segment: 'inspection-home', title: 'Inspection Home', icon: <DashboardIcon /> },
    { segment: 'trip-inspection-register', title: 'Trip Inspection Register', icon: <ListAltIcon /> },
    { segment: 'ticket-pass-validation', title: 'Ticket / Pass Validation', icon: <VerifiedUserIcon /> },
    { segment: 'penalty-enforcement', title: 'Penalty / Enforcement', icon: <GavelIcon /> },
    { segment: 'inspection-history', title: 'Inspection History', icon: <HistoryIcon /> },
    { segment: 'inspector-reports', title: 'Inspector Reports', icon: <Assignment /> },

    // 7.0 Depot Accounts
    {
      kind: 'header',
      title: 'Depot Accounts',
    },
    { segment: 'depot-accounts-dashboard', title: 'Depot Accounts Dashboard', icon: <DashboardIcon /> },
    {
      segment: 'conductor-settlements',
      title: 'Conductor Settlements',
      icon: <HandshakeIcon />,
      children: [
        { segment: 'settlement-register', title: 'Settlement Register', icon: <ListAltIcon /> },
        { segment: 'settlement-detail', title: 'Settlement Detail', icon: <Assignment /> },
        { segment: 'variance-register', title: 'Variance Register', icon: <WarningAmberIcon /> },
      ],
    },
    {
      segment: 'bus-revenue',
      title: 'Bus-wise Revenue',
      icon: <AttachMoney />,
      children: [
        { segment: 'bus-revenue-summary', title: 'Bus Revenue Summary', icon: <SummarizeIcon /> },
        { segment: 'bus-revenue-detail', title: 'Bus Revenue Detail', icon: <Assignment /> },
        { segment: 'trip-contribution', title: 'Trip Contribution', icon: <AccountBalanceWalletIcon /> },
      ],
    },
    {
      segment: 'depot-cashbook',
      title: 'Depot Cashbook / Receipts',
      icon: <ReceiptIcon />,
      children: [
        { segment: 'cashbook-register', title: 'Cashbook Register', icon: <ListAltIcon /> },
        { segment: 'receipt-register', title: 'Receipt Register', icon: <ReceiptIcon /> },
        { segment: 'bank-deposit-register', title: 'Bank Deposit Register', icon: <SavingsIcon /> },
      ],
    },
    {
      segment: 'ho-receivables',
      title: 'Head Office Receivables',
      icon: <AccountBalanceIcon />,
      children: [
        { segment: 'receivable-summary', title: 'Receivable Summary', icon: <SummarizeIcon /> },
        { segment: 'receivable-ledger', title: 'Receivable Ledger', icon: <ListAltIcon /> },
        { segment: 'transfer-confirmation', title: 'Transfer Confirmation', icon: <CheckCircleOutlineIcon /> },
      ],
    },
    { segment: 'counter-booking-fee', title: 'Counter Booking Fee', icon: <RequestQuoteOutlined /> },
    { segment: 'season-tickets-depot', title: 'Season Tickets', icon: <CardMembershipIcon /> },
    { segment: 'inter-depot-settlement', title: 'Inter-Depot Settlement', icon: <CompareArrowsIcon /> },
    { segment: 'discrepancy-mis', title: 'Discrepancy / Shortage MIS', icon: <WarningAmberIcon /> },
    { segment: 'breakdown-refund-mgmt', title: 'Breakdown Refund Management', icon: <BuildIcon /> },
    { segment: 'depot-statements', title: 'Depot Statements & Audit', icon: <Assignment /> },

    // 8.0 Depot Main Cashier
    {
      kind: 'header',
      title: 'Depot Cashier',
    },
    { segment: 'cashier-dashboard', title: 'Cashier Dashboard', icon: <DashboardIcon /> },
    { segment: 'booking-charge-receipts', title: 'Booking Charge Receipts', icon: <ReceiptIcon /> },
    { segment: 'conductor-settlement-receipts', title: 'Conductor Settlement Receipts', icon: <HandshakeIcon /> },
    { segment: 'cash-consolidation', title: 'Cash Consolidation', icon: <LocalAtmIcon /> },
    { segment: 'daily-cash-summary', title: 'Daily Cash Summary', icon: <SummarizeIcon /> },
    { segment: 'bank-deposit-prep', title: 'Bank Deposit Preparation', icon: <AccountBalanceIcon /> },
    { segment: 'bank-deposit-register', title: 'Bank Deposit Register', icon: <SavingsIcon /> },
    { segment: 'hgh-payable-summary', title: 'HGH Payable Summary', icon: <PaymentsOutlined /> },
    { segment: 'monthly-hgh-transfer', title: 'Monthly HGH Transfer', icon: <SwapHorizIcon /> },
    { segment: 'cashier-reports', title: 'Cashier Reports', icon: <Assignment /> },

    // 9.0 Depot Operations
    {
      kind: 'header',
      title: 'Depot Operations',
    },
    { segment: 'depot-dashboard', title: 'Depot Dashboard', icon: <DashboardIcon /> },
    { segment: 'bus-allocation', title: 'Bus Allocation', icon: <DirectionsBusIcon /> },
    { segment: 'crew-allocation', title: 'Crew Allocation', icon: <GroupIcon /> },
    { segment: 'trip-monitoring', title: 'Trip Monitoring', icon: <MonitorHeartIcon /> },
    { segment: 'breakdown-monitoring', title: 'Breakdown Monitoring', icon: <BusAlert /> },
    { segment: 'route-schedule-performance', title: 'Route / Schedule Performance', icon: <SpeedIcon /> },
    { segment: 'cash-settlement-alerts', title: 'Cash & Settlement Alerts', icon: <WarningAmberIcon /> },
    { segment: 'depot-mis', title: 'Depot MIS', icon: <AnalyticsIcon /> },


    // 10.0 Head Office Accounts
    {
      kind: 'header',
      title: 'Head Office Accounts',
    },
    { segment: 'finance-overview', title: 'Finance Overview', icon: <DashboardIcon /> },
    { segment: 'depot-payables', title: 'Depot Payables', icon: <PaymentsOutlined /> },
    { segment: 'settlement-batches', title: 'Settlement Batches', icon: <CompareArrowsIcon /> },
    { segment: 'transfer-register', title: 'Transfer Register', icon: <SwapHorizIcon /> },
    { segment: 'revenue-channels', title: 'Revenue Channels', icon: <AccountTreeIcon /> },
    { segment: 'liabilities', title: 'Liabilities', icon: <RequestQuoteOutlined /> },
    { segment: 'reconciliation', title: 'Reconciliation', icon: <CompareArrowsIcon /> },
    { segment: 'ho-reports', title: 'Reports', icon: <Assignment /> },
    { segment: 'admin-views', title: 'Admin Views', icon: <AdminPanelSettingsIcon /> },


    // 11.0 HO Finance Management
    {
      kind: 'header',
      title: 'HO Finance Management',
    },
    { segment: 'cfo-dashboard', title: 'CFO Dashboard', icon: <DashboardIcon /> },
    { segment: 'revenue-summary', title: 'Revenue Summary', icon: <AttachMoney /> },
    { segment: 'settlement-summary', title: 'Settlement Summary', icon: <CompareArrowsIcon /> },
    { segment: 'receivables-payables', title: 'Receivables & Payables', icon: <AccountBalanceWalletIcon /> },
    { segment: 'refund-oversight', title: 'Refund Oversight', icon: <MoneyOffIcon /> },
    { segment: 'govt-billing-oversight', title: 'Government Billing Oversight', icon: <PolicyIcon /> },
    { segment: 'partner-settlement', title: 'Partner Settlement Oversight', icon: <HandshakeIcon /> },
    { segment: 'finance-mis', title: 'Finance MIS', icon: <AnalyticsIcon /> },

    // 12.0 Government Pass Management
    {
      kind: 'header',
      title: 'Pass Management',
    },
    { segment: 'agency-master', title: 'Agency Master', icon: <BusinessIcon /> },
    { segment: 'pass-product-setup', title: 'Pass Product Setup', icon: <TuneIcon /> },
    { segment: 'pass-assignment', title: 'Pass Assignment', icon: <Assignment /> },
    { segment: 'pass-register', title: 'Pass Register', icon: <ListAltIcon /> },
    { segment: 'monthly-billing', title: 'Monthly Billing', icon: <ReceiptIcon /> },
    { segment: 'agency-receivables', title: 'Agency Receivables', icon: <AccountBalanceWalletIcon /> },
    { segment: 'govt-pass-reports', title: 'Government Pass Reports', icon: <SummarizeIcon /> },

    {
      kind: 'header',
      title: 'Travel Card Management',
    },
    { segment: 'travel-card-dashboard', title: 'Travel Card Dashboard', icon: <DashboardIcon /> },
    { segment: 'card-issuance', title: 'Card Issuance', icon: <AddCircleIcon /> },
    { segment: 'card-topup', title: 'Card Top-up', icon: <AttachMoney /> },
    { segment: 'card-ledger', title: 'Card Ledger', icon: <ListAltIcon /> },
    { segment: 'card-usage-monitoring', title: 'Card Usage Monitoring', icon: <MonitorHeartIcon /> },
    { segment: 'card-blocking', title: 'Card Blocking / Replacement', icon: <CancelOutlinedIcon /> },
    { segment: 'travel-card-liability', title: 'Travel Card Liability', icon: <RequestQuoteOutlined /> },
    { segment: 'travel-card-settlement', title: 'Travel Card Settlement', icon: <CompareArrowsIcon /> },
    { segment: 'travel-card-reports', title: 'Travel Card Reports', icon: <SummarizeIcon /> },

    {
      kind: 'header',
      title: 'Season Ticket Management',
    },
    { segment: 'season-product-setup', title: 'Product Setup', icon: <TuneIcon /> },
    { segment: 'season-ticket-issuance', title: 'Season Ticket Issuance', icon: <AddCircleIcon /> },
    { segment: 'season-ticket-register', title: 'Season Ticket Register', icon: <ListAltIcon /> },
    { segment: 'usage-monitoring', title: 'Usage Monitoring', icon: <MonitorHeartIcon /> },
    { segment: 'revenue-register', title: 'Revenue Register', icon: <AttachMoney /> },
    { segment: 'cross-depot-usage', title: 'Cross-Depot Usage Summary', icon: <CompareArrowsIcon /> },
    { segment: 'season-ticket-reports', title: 'Season Ticket Reports', icon: <SummarizeIcon /> },


    // 15.0 Digital Channel Operations
    {
      kind: 'header',
      title: 'Digital Channel Operations',
    },
    { segment: 'digital-channel-dashboard', title: 'Digital Channel Dashboard', icon: <DashboardIcon /> },
    { segment: 'online-booking-monitoring', title: 'Online Booking Monitoring', icon: <MonitorHeartIcon /> },
    { segment: 'online-onboard-monitoring', title: 'Online Onboard Monitoring', icon: <MonitorHeartIcon /> },
    { segment: 'payment-monitoring', title: 'Payment Success / Failure', icon: <WarningAmberIcon /> },
    { segment: 'failed-tap-monitoring', title: 'Failed Tap Monitoring', icon: <ErrorOutlineIcon /> },
    { segment: 'digital-adoption-mis', title: 'Digital Adoption MIS', icon: <AnalyticsIcon /> },
    { segment: 'channel-performance', title: 'Channel Performance', icon: <SpeedIcon /> },
    { segment: 'digital-exceptions', title: 'Digital Exceptions', icon: <ErrorOutlineIcon /> },

    {
      kind: 'header',
      title: 'Refund Management',
    },
    { segment: 'refund-dashboard', title: 'Refund Dashboard', icon: <DashboardIcon /> },
    { segment: 'refund-requests', title: 'Refund Requests', icon: <PendingActionsIcon /> },
    { segment: 'refund-approval', title: 'Refund Approval', icon: <CheckCircleOutlineIcon /> },
    { segment: 'refund-processing', title: 'Refund Processing', icon: <SwapHorizIcon /> },
    { segment: 'refund-register', title: 'Refund Register', icon: <ListAltIcon /> },
    { segment: 'breakdown-digital-refunds', title: 'Breakdown Digital Refunds', icon: <BusAlert /> },
    { segment: 'refund-reports', title: 'Refund Reports', icon: <SummarizeIcon /> },

    {
      kind: 'header',
      title: 'Breakdown Management',
    },
    { segment: 'breakdown-dashboard', title: 'Breakdown Dashboard', icon: <DashboardIcon /> },
    { segment: 'breakdown-event-register', title: 'Breakdown Event Register', icon: <ListAltIcon /> },
    { segment: 'depot-confirmation', title: 'Depot Confirmation', icon: <CheckCircleOutlineIcon /> },
    { segment: 'conductor-refund-report', title: 'Conductor Refund Report', icon: <Assignment /> },
    { segment: 'digital-refund-report', title: 'Digital Refund Report', icon: <PhoneAndroidIcon /> },
    { segment: 'ho-auth-queue', title: 'HO Authorization Queue', icon: <PendingActionsIcon /> },
    { segment: 'breakdown-closure', title: 'Breakdown Closure', icon: <CancelOutlinedIcon /> },
    { segment: 'breakdown-analysis', title: 'Breakdown Analysis', icon: <AnalyticsIcon /> },


    // 18.0 Reconciliation & Settlement
    {
      kind: 'header',
      title: 'Reconciliation & Analytics',
    },
    { segment: 'reconciliation-dashboard', title: 'Reconciliation Dashboard', icon: <DashboardIcon /> },
    { segment: 'channel-reconciliation', title: 'Channel Reconciliation', icon: <SwapHorizIcon /> },
    { segment: 'bank-reconciliation', title: 'Bank Reconciliation', icon: <AccountBalanceIcon /> },
    { segment: 'transfer-matching', title: 'Transfer Matching', icon: <CompareArrowsIcon /> },
    { segment: 'settlement-variances', title: 'Settlement Variances', icon: <WarningAmberIcon /> },
    { segment: 'exception-queue', title: 'Exception Queue', icon: <ErrorOutlineIcon /> },
    { segment: 'inter-node-settlement', title: 'Inter-Node Settlement Monitor', icon: <AccountTreeIcon /> },


    // 19.0 MIS & Analytics
    {
      kind: 'header',
      title: 'MIS & Analytics',
    },
    { segment: 'executive-dashboard', title: 'Executive Dashboard', icon: <DashboardIcon /> },
    { segment: 'revenue-analytics', title: 'Revenue Analytics', icon: <AttachMoney /> },
    { segment: 'route-analytics', title: 'Route Analytics', icon: <RouteIcon /> },
    { segment: 'schedule-analytics', title: 'Schedule Analytics', icon: <CalendarMonthIcon /> },
    { segment: 'bus-analytics', title: 'Bus Analytics', icon: <DirectionsBusIcon /> },
    { segment: 'digital-analytics', title: 'Digital Analytics', icon: <PhoneAndroidIcon /> },
    { segment: 'finance-analytics', title: 'Finance Analytics', icon: <AccountBalanceWalletIcon /> },
    { segment: 'inspection-analytics', title: 'Inspection Analytics', icon: <GavelIcon /> },
    { segment: 'operations-analytics', title: 'Operations Analytics', icon: <SpeedIcon /> },


    // 20.0 AI Alert & Analysis
    {
      kind: 'header',
      title: 'AI Alert & Analysis',
    },
    { segment: 'ai-dashboard', title: 'AI Dashboard', icon: <DashboardIcon /> },
    { segment: 'alert-work-queue', title: 'Alert Work Queue', icon: <PendingActionsIcon /> },
    { segment: 'alert-detail', title: 'Alert Detail', icon: <Assignment /> },
    { segment: 'alert-cases', title: 'Alert Cases', icon: <ListAltIcon /> },
    { segment: 'alert-rules', title: 'Alert Rules', icon: <TuneIcon /> },
    { segment: 'prediction-scores', title: 'Prediction Scores', icon: <AutoGraphIcon /> },
    { segment: 'risk-heatmaps', title: 'Risk Heatmaps', icon: <HeatPumpIcon /> },
    { segment: 'revenue-risk-alerts', title: 'Revenue Risk Alerts', icon: <WarningAmberIcon /> },
    { segment: 'cash-risk-alerts', title: 'Cash Risk Alerts', icon: <LocalAtmIcon /> },
    { segment: 'digital-risk-alerts', title: 'Digital Risk Alerts', icon: <PhoneAndroidIcon /> },
    { segment: 'product-abuse-alerts', title: 'Product Abuse Alerts', icon: <ErrorOutlineIcon /> },
    { segment: 'breakdown-risk-alerts', title: 'Breakdown Risk Alerts', icon: <BusAlert /> },
    { segment: 'settlement-risk-alerts', title: 'Settlement Risk Alerts', icon: <CompareArrowsIcon /> },
    { segment: 'feedback-resolution-log', title: 'Feedback & Resolution Log', icon: <HistoryIcon /> },

    // 21.0 Executive / Board Dashboard
    {
      kind: 'header',
      title: 'Executive / Board Dashboard',
    },
    { segment: 'executive-kpi-dashboard', title: 'Executive KPI Dashboard', icon: <DashboardIcon /> },
    { segment: 'revenue-overview', title: 'Revenue Overview', icon: <AttachMoney /> },
    { segment: 'route-schedule-performance', title: 'Route & Schedule Performance', icon: <SpeedIcon /> },
    { segment: 'digital-performance-summary', title: 'Digital Performance Summary', icon: <PhoneAndroidIcon /> },
    { segment: 'channel-performance', title: 'Channel Performance', icon: <AccountTreeIcon /> },
    { segment: 'risk-exception-summary', title: 'Risk & Exception Summary', icon: <WarningAmberIcon /> },
    { segment: 'ai-executive-alerts', title: 'AI Executive Alerts', icon: <SmartToyIcon /> },


    // 22.0 Reports & Exports
    {
      kind: 'header',
      title: 'Reports & Support',
    },
    { segment: 'standard-reports', title: 'Standard Reports', icon: <ListAltIcon /> },
    { segment: 'financial-reports', title: 'Financial Reports', icon: <AttachMoney /> },
    { segment: 'operational-reports', title: 'Operational Reports', icon: <Assignment /> },
    { segment: 'export-center', title: 'Export Center', icon: <FileDownloadIcon /> },
    { segment: 'scheduled-reports', title: 'Scheduled Reports', icon: <ScheduleSendIcon /> },


    // 23.0 Notifications & Work Queues
    {
      kind: 'header',
      title: 'Notifications & Queues',
    },
    { segment: 'notifications', title: 'Notifications', icon: <NotificationsIcon /> },
    { segment: 'pending-approvals', title: 'Pending Approvals', icon: <PendingActionsIcon /> },
    { segment: 'pending-handovers', title: 'Pending Handovers', icon: <HandshakeIcon /> },
    { segment: 'pending-settlements', title: 'Pending Settlements', icon: <CompareArrowsIcon /> },
    { segment: 'pending-refunds', title: 'Pending Refunds', icon: <MoneyOffIcon /> },
    { segment: 'pending-reconciliations', title: 'Pending Reconciliations', icon: <SwapHorizIcon /> },
    { segment: 'pending-alerts', title: 'Pending Alerts', icon: <WarningAmberIcon /> },
    { segment: 'escalations', title: 'Escalations', icon: <EscalatorWarningIcon /> },


    // 24.0 Help & Support
    {
      kind: 'header',
      title: 'Help & Support',
    },
    { segment: 'user-guide', title: 'User Guide', icon: <ListAltIcon /> },
    { segment: 'sop-library', title: 'SOP Library', icon: <Assignment /> },
    { segment: 'faq', title: 'FAQ', icon: <HelpIcon /> },
    { segment: 'support-tickets', title: 'Support Tickets', icon: <ContactPhoneIcon /> },
    { segment: 'training-material', title: 'Training Material', icon: <SchoolIcon /> },
    { segment: 'release-notes', title: 'Release Notes', icon: <HistoryIcon /> },

  ],

  Admin: [],
  OperationManager: [],
  OperationStaff: [],
  CallCenterStaff: [],
  DeportOperator: [],
  AccountDepartment: [],
};

const demoTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-toolpad-color-scheme',
  },
  colorSchemes: { light: true, dark: false },
  defaultColorScheme: 'light',
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
});


function DemoPageContent({ pathname }) {
  sessionStorage.setItem('currentPath', pathname);

  let content;

  switch (pathname) {
    case '/':
      content = <Dashboard />;
      break;
    case '/dashboard':
      content = <Dashboard />;
      break;
    case '/finance-page':
      content = <FinancePage />;
      break;
    case '/daily-channel-reconciliation':
      content = <DailyChannelReconciliationPage />;
      break;
    case '/refund-register-page':
      content = <RefundRegisterPage />;
      break;
    case '/travel-card-ledger-summary':
      content = <TravelCardLedgerSummaryPage />;
      break;
    case '/conductor-pos-route-assignment':
      content = <ConductorPOSRouteAssignment />;
      break;
    case '/bus-breakdown-refund-page':
      content = <BusBreakdownRefundPage />;
      break;
    case '/bus-breakdown-analysis':
      content = <BusBreakdownAnalysisPage />;
      break;
    case '/ai-alert-analysis':
      content = <AIAlertAnalysisPage />;
      break;
    case '/revenue-trends-page':
      content = <RevenueTrendsPage />;
      break;
    case '/route-schedule-page':
      content = <RouteSchedulePage />;
      break;
    case '/risk-exceptions-page':
      content = <RiskExceptionsPage />;
      break;
    case '/bus-profitability-page':
      content = <BusProfitabilityPage />;
      break;
    case '/channel-performance-page':
      content = <ChannelPerformancePage />;
      break;
    case '/schedule-efficiency-page':
      content = <ScheduleEfficiencyPage />;
      break;

    // case '/route-management':
    //   content = <RouteManagement />;
    //   break;
    // case '/route-management/all-route':
    //   content = <RouteManagement />;
    //   break;
    // case '/route-management/active-route':
    //   content = <ActiveRouteManagement />;
    //   break;
    // case '/route-management/manageBusPoints':
    //   content = <ManageBusPoints />;
    //   break;
    // case '/route-management/manageBusFareBreaks':
    //   content = <ManageBusFareBreaks />;
    //   break;

    default:
      content = <Typography>No page found</Typography>;
  }

  return (
    <Box sx={{ py: 0, pb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {content}

      {/* Alert */}
      {/* <CustomAlert severity="error" message="Sample alert message" /> */}

    </Box>
  );
}

DemoPageContent.propTypes = {
  pathname: PropTypes.string.isRequired,
};


let routval = sessionStorage.getItem('currentPath') || '/';

export function setroutval(newRoute, RouteID) {
  routval = newRoute;
  sessionStorage.setItem('currentPath', newRoute);
  sessionStorage.setItem('currentValueID', RouteID);
  window.location.reload();
}

function DashboardLayoutAccount({ window, onLogout }) {
  // const { window } = props;




  const [session, setSession] = React.useState({
    user: {
      name: 'Super Admin',
      email: 'super@gmail.com',
      type: 'SuperAdmin',
      // image: 'https://avatars.githubusercontent.com/u/19550456',
    },
  });



  const authentication = React.useMemo(() => {
    return {
      signIn: () => {
        setSession({
          user: {
            name: 'Super Admin',
            email: 'super@gmail.com',
            type: 'SuperAdmin',
            // image: 'https://avatars.githubusercontent.com/u/19550456',
          },
        });
      },
      signOut: () => {
        setSession(null);
        onLogout();
      },
    };
  }, [onLogout]);

  const router = useDemoRouter(routval);

  const branding = {
    logo: (
      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {/* <Logo style={{ width: '150px' }} /> */}
        <img src={Logo} alt="SRT Logo" style={{}} />
      </Box>
    ),
    title: '',
  };


  const filteredNavigation = NAVIGATION[session?.user?.type] || [];

  // Remove this const when copying and pasting into your project.
  const demoWindow = window !== undefined ? window() : undefined;

  return (
    // preview-start
    <AppProvider
      session={session}
      authentication={authentication}
      navigation={filteredNavigation}
      router={router}
      theme={demoTheme}
      window={demoWindow}
      branding={branding}

    >
      <DashboardLayout defaultSidebarCollapsed
        sx={{
          '& .MuiDrawer-paper': {
            '& > *': {
              overflowY: 'auto !important',
              scrollbarWidth: 'none !important',
              msOverflowStyle: 'none !important',
              '&::-webkit-scrollbar': {
                width: '0 !important',
                display: 'none !important'
              }
            }
          }
        }}
      >
        <DemoPageContent pathname={router.pathname} />
      </DashboardLayout>
    </AppProvider>
    // preview-end
  );
}

DashboardLayoutAccount.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * Remove this when copying and pasting into your project.
   */
  window: PropTypes.func,
};

export default DashboardLayoutAccount;
